'use client';

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { FeatureCard } from '@/components/pageComponents/global/FeatureCard';
import { PageAboutUsSchema } from '@/sanity/pages/PageAboutUsTemplate';

const ease = [0.25, 0.1, 0.25, 1] as const;

export default function IntroSection({ data }: { data: PageAboutUsSchema }) {
  const cards = data.introCards ?? [];
  const [hoveredIndex, setHoveredIndex] = useState(0);
  const [hoverEnabled, setHoverEnabled] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    const updateHover = () => setHoverEnabled(mediaQuery.matches);
    updateHover();
    mediaQuery.addEventListener('change', updateHover);
    return () => mediaQuery.removeEventListener('change', updateHover);
  }, []);

  if (!data.introTitle && !cards.length) return null;

  return (
    <section className="relative w-full bg-white pt-[7rem] pb-[3rem] lg:pt-[16rem] lg:pb-[15rem]">
      <div className="mx-auto flex w-[min(140rem,calc(100%-4rem))] lg:w-[min(140rem,calc(100%-8rem))] flex-col items-center gap-[6rem] lg:gap-[8rem]">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease }}
          className="flex flex-col items-center gap-[2rem] max-w-[80rem]"
        >
          {data.introChip && (
            <p className="mb-0 font-blacker italic text-[2rem] leading-[1.5] text-black">
              {data.introChip}
            </p>
          )}
          {data.introTitle && (
            <div className="richtext prose-em:font-normal prose-em:whitespace-nowrap [&_*]:[text-wrap:unset]">
              <PortableText
                value={data.introTitle}
                components={richTextComponent}
              />
            </div>
          )}
        </motion.div>

        {!!cards.length && (
          <div className="flex w-fit lg:flex-row flex-col  gap-[2.25rem] lg:gap-[3.75rem]">
            {cards.map((card, index) => (
              <FeatureCard
                key={card._key ?? index}
                card={card}
                active={hoveredIndex === index}
                onActivate={() => setHoveredIndex(index)}
                hoverEnabled={hoverEnabled}
                variant="about-us"
                className="max-w-[46rem]"
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
